/**
 * Formats date-like values for chart axes, tooltips, pie labels, and data tables.
 *
 * Handles:
 *   - YYYYMM (6-digit), including numeric JSON values (e.g. 202503 → "Mar 2025")
 *   - YYYY-MM
 *   - YYYYMMDD (8-digit)
 *   - ISO date / datetime strings
 *
 * Returns the original `value` when it does not look like a supported date.
 */
export function formatChartAxisDateValue(value) {
  if (value === null || value === undefined) return value;

  let str = String(value).trim();

  const asNum = Number(str);
  if (
    Number.isFinite(asNum) &&
    asNum >= 190001 &&
    asNum <= 210012 &&
    Math.abs(asNum - Math.round(asNum)) < 1e-9
  ) {
    str = String(Math.round(asNum));
  }

  if (/^\d{6}$/.test(str)) {
    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10);
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12) {
      try {
        const date = new Date(year, month - 1, 1);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
        }
      } catch {
        return value;
      }
    }
    return value;
  }

  if (/^\d{4}-\d{2}$/.test(str)) {
    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(5, 7), 10);
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12) {
      try {
        const date = new Date(year, month - 1, 1);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
        }
      } catch {
        return value;
      }
    }
    return value;
  }

  if (/^\d{8}$/.test(str)) {
    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10);
    const day = parseInt(str.substring(6, 8), 10);
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      try {
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }
      } catch {
        return value;
      }
    }
    return value;
  }

  if (/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/.test(str)) {
    try {
      const normalised = str.replace(" ", "T");
      const date = new Date(normalised);
      if (!isNaN(date.getTime())) {
        if (str.includes("T") || str.includes(" ")) {
          return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
          });
        }
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    } catch {
      return value;
    }
  }

  return value;
}
